import type { DbKey, WithDbId } from "adl-gen/common/db";
import type { UserMembership } from "adl-gen/ferovinum/app/api";
import type {
  Carrier,
  Currency,
  GuardedFeature,
  Organisation,
  Purchaser,
  StorageLocation,
  Supplier,
} from "adl-gen/ferovinum/app/db";
import React, { useContext, useMemo } from "react";
import { assertNever } from "utils/hx/util/types";
import type { LoadingValue } from "utils/utility-types";

export type SelectedEntity = { membership: LoadingValue<UserMembership> } & (
  | {
      type: "org";
      value: WithDbId<Organisation>;
    }
  | {
      type: "storageLoc";
      value: WithDbId<StorageLocation>;
    }
  | {
      type: "carrier";
      value: WithDbId<Carrier>;
    }
  | {
      type: "supplier";
      value: WithDbId<Supplier>;
    }
  | {
      type: "purchaser";
      value: WithDbId<Purchaser>;
    }
  | {
      type: undefined;
    }
);

export const SelectedEntityContext = React.createContext<SelectedEntity>({
  type: undefined,
  membership: { state: "loading" },
});

export const useSelectedOrgId = (): DbKey<Organisation> | undefined => {
  const selectedEntities = useContext(SelectedEntityContext);
  if (selectedEntities.type !== "org") {
    return undefined;
  }
  return selectedEntities.value.id;
};

export const useSelectedOrg = (): WithDbId<Organisation> | undefined => {
  const selectedEntities = useContext(SelectedEntityContext);
  if (selectedEntities.type !== "org") {
    return undefined;
  }
  return selectedEntities.value;
};

export const useSelectedOrgFeatures = (): GuardedFeature[] => {
  const selectedEntities = useContext(SelectedEntityContext);

  return useMemo(() => {
    if (
      selectedEntities.type === "org" &&
      selectedEntities.membership.state === "success"
    ) {
      const orgId = selectedEntities.value.id;
      return (
        selectedEntities.membership.value.orgFeatures.find(
          (e) => e.key === orgId,
        )?.value ?? []
      );
    }
    return [];
  }, [selectedEntities]);
};

export const useSelectedCarrier = (): WithDbId<Carrier> | undefined => {
  const selectedEntities = useContext(SelectedEntityContext);
  if (selectedEntities.type !== "carrier") {
    return undefined;
  }
  return selectedEntities.value;
};

export const useSelectedStorageLocation = ():
  | WithDbId<StorageLocation>
  | undefined => {
  const selectedEntities = useContext(SelectedEntityContext);
  if (selectedEntities.type !== "storageLoc") {
    return undefined;
  }
  return selectedEntities.value;
};

export const useSelectedSupplier = (): WithDbId<Supplier> | undefined => {
  const selectedEntities = useContext(SelectedEntityContext);
  if (selectedEntities.type !== "supplier") {
    return undefined;
  }
  return selectedEntities.value;
};

export const useSelectedPurchaser = (): WithDbId<Purchaser> | undefined => {
  const selectedEntities = useContext(SelectedEntityContext);
  if (selectedEntities.type !== "purchaser") {
    return undefined;
  }
  return selectedEntities.value;
};

export const useMembership = (): LoadingValue<UserMembership> => {
  const selectedEntities = useContext(SelectedEntityContext);
  return selectedEntities.membership;
};

export const useSelectedEntity = () => useContext(SelectedEntityContext);

export const useSettlementCurrency = (): Currency => {
  const selectedEntity = useSelectedEntity();
  if (selectedEntity.type === "org" || selectedEntity.type === "purchaser") {
    return selectedEntity.value.value.currency;
  }
  if (selectedEntity.type === "storageLoc") {
    // TODO(barry): Add currency to storage location
    return "GBP";
  }
  if (selectedEntity.type === "carrier" || selectedEntity.type === "supplier") {
    throw new Error(
      `Entity type ${selectedEntity.type} does not have a settlement currency`,
    );
  }
  if (!selectedEntity.type) {
    throw new Error("Cannot retrieve settlement currency in this state");
  }
  assertNever(selectedEntity);
};
